import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api.ts'
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Settings2,
  Bug,
  Link2,
  Link2Off,
  RefreshCcw,
  Users,
} from 'lucide-react'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Input,
  Modal,
  PageHeader,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  actionsColumn,
  dateColumn,
  textColumn,
  useToast,
  ErrorState,
  type ColumnDef,
} from '@/components/ui'

interface EsetLicense {
  id: string
  productName: string
  seats: number
  usedSeats: number
  expirationDate: string
  status: 'active' | 'expired' | 'expiring_soon'
  customerId?: string
  customerName?: string
}

interface EsetThreatReport {
  period: string
  totalThreats: number
  resolvedThreats: number
  unresolvedThreats: number
  topThreats: Array<{ name: string; type: string; count: number }>
  affectedDevices: number
}

interface EsetStats {
  totalLicenses: number
  expiringLicenses: number
  expiredLicenses: number
  protectedDevices: number
  activeThreats: number
}

export function EsetOverviewPage() {
  const a = useAnonymize()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [seatDialog, setSeatDialog] = useState<{ license: EsetLicense; newSeats: number } | null>(null)

  const stats = useQuery<EsetStats>({
    queryKey: ['eset-stats'],
    queryFn: () => api.get('/eset/stats').then((r) => r.data),
    retry: false,
  })

  const licenses = useQuery<EsetLicense[]>({
    queryKey: ['eset-licenses'],
    queryFn: () => api.get('/eset/licenses').then((r) => r.data),
    retry: false,
  })

  const threats = useQuery<EsetThreatReport[]>({
    queryKey: ['eset-threats'],
    queryFn: () => api.get('/eset/threats').then((r) => r.data),
    retry: false,
  })

  const updateSeats = useMutation({
    mutationFn: ({ licenseId, seats }: { licenseId: string; seats: number }) =>
      api.put(`/eset/licenses/${licenseId}/seats`, { seats }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eset-licenses'] })
      queryClient.invalidateQueries({ queryKey: ['eset-stats'] })
      toast({ title: 'Seats aktualisiert', tone: 'success' })
      setSeatDialog(null)
    },
    onError: () => {
      toast({
        title: 'Fehler beim Speichern',
        description: 'Bitte erneut versuchen.',
        tone: 'danger',
      })
    },
  })

  const columns = useMemo<ColumnDef<EsetLicense, unknown>[]>(
    () => [
      {
        id: 'customerName',
        accessorKey: 'customerName',
        header: 'Kunde',
        cell: ({ row }) => {
          const name = row.original.customerName
          return name ? a.name(name) : '—'
        },
      },
      textColumn<EsetLicense>('productName', 'Produkt'),
      {
        id: 'seats',
        accessorKey: 'seats',
        header: 'Seats',
        size: 120,
        cell: ({ row }) => (
          <span className="tabular-nums">
            <span className="font-medium text-gray-900">{row.original.usedSeats}</span>
            <span className="text-gray-400"> / {row.original.seats}</span>
          </span>
        ),
      },
      dateColumn<EsetLicense>('expirationDate', 'Ablauf', { size: 140 }),
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      actionsColumn<EsetLicense>(
        'Aktion',
        (license) => (
          <Button
            variant="ghost"
            size="xs"
            leadingIcon={<Settings2 />}
            onClick={(event) => {
              event.stopPropagation()
              setSeatDialog({ license, newSeats: license.seats })
            }}
          >
            Seats
          </Button>
        ),
        { size: 120 },
      ),
    ],
    [a],
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="ESET MSP"
        description="Lizenzen, Bedrohungen, Seat-Verwaltung und Kunden-Sync"
      />

      <Tabs defaultValue="lizenzen">
        <TabsList>
          <TabsTrigger value="lizenzen">
            <Shield className="w-4 h-4" />
            Lizenzen &amp; Bedrohungen
          </TabsTrigger>
          <TabsTrigger value="kunden">
            <Users className="w-4 h-4" />
            Kunden-Sync
          </TabsTrigger>
        </TabsList>

        <TabsContent value="lizenzen">
          <div className="space-y-6">
      {/* Stats */}
      {stats.isError && (
        <ErrorState
          size="sm"
          title="Statistiken konnten nicht geladen werden"
          description="ESET MSP nicht konfiguriert oder Verbindungsfehler."
          details={stats.error ? JSON.stringify(stats.error, Object.getOwnPropertyNames(stats.error as object), 2) : undefined}
          onRetry={() => stats.refetch()}
        />
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          label="Lizenzen"
          value={stats.data?.totalLicenses}
          loading={stats.isLoading}
          icon={<Shield className="w-5 h-5 text-green-500" />}
          tone="gray"
        />
        <StatCard
          label="Ablaufend (30T)"
          value={stats.data?.expiringLicenses}
          loading={stats.isLoading}
          icon={<AlertTriangle className="w-5 h-5 text-yellow-500" />}
          tone={stats.data && stats.data.expiringLicenses > 0 ? 'yellow' : 'gray'}
        />
        <StatCard
          label="Abgelaufen"
          value={stats.data?.expiredLicenses}
          loading={stats.isLoading}
          icon={<XCircle className="w-5 h-5 text-red-500" />}
          tone={stats.data && stats.data.expiredLicenses > 0 ? 'red' : 'gray'}
        />
        <StatCard
          label="Geschützte Geräte"
          value={stats.data?.protectedDevices}
          loading={stats.isLoading}
          icon={<CheckCircle2 className="w-5 h-5 text-green-500" />}
          tone="green"
        />
        <StatCard
          label="Aktive Bedrohungen"
          value={stats.data?.activeThreats}
          loading={stats.isLoading}
          icon={<Bug className="w-5 h-5 text-red-500" />}
          tone={stats.data && stats.data.activeThreats > 0 ? 'red' : 'gray'}
        />
      </div>

      {/* Threat Reports */}
      {threats.data && threats.data.length > 0 ? (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Bug className="w-5 h-5 text-red-600" />
            <h2 className="text-h4 text-gray-800">Bedrohungsberichte</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {threats.data.map((report) => (
              <Card key={report.period}>
                <Card.Body className="space-y-3">
                  <p className="text-body font-semibold text-gray-900">{report.period}</p>
                  <div className="space-y-1 text-small">
                    <Row label="Gesamt" value={report.totalThreats} />
                    <Row
                      label="Behoben"
                      value={report.resolvedThreats}
                      valueClassName="text-green-600 font-medium"
                    />
                    <Row
                      label="Offen"
                      value={report.unresolvedThreats}
                      valueClassName="text-red-600 font-medium"
                    />
                    <Row label="Betroffene Geräte" value={report.affectedDevices} />
                  </div>
                  {report.topThreats.length > 0 ? (
                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-caption text-gray-400 uppercase font-semibold mb-1">
                        Top-Bedrohungen
                      </p>
                      {report.topThreats.slice(0, 3).map((t) => (
                        <div key={t.name} className="flex justify-between text-caption mt-1">
                          <span className="text-gray-600 truncate mr-2">{t.name}</span>
                          <span className="text-gray-900 font-medium">{t.count}x</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </Card.Body>
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      {/* License Table */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-600" />
          <h2 className="text-h4 text-gray-800">Lizenzen</h2>
        </div>

        <DataTable<EsetLicense>
          data={licenses.data ?? []}
          columns={columns}
          getRowId={(l) => l.id}
          loading={licenses.isLoading}
          error={
            licenses.isError
              ? 'ESET MSP nicht konfiguriert oder Verbindungsfehler.'
              : undefined
          }
          onRetry={() => licenses.refetch()}
          emptyState={
            <EmptyState
              title="Keine Lizenzen gefunden"
              icon={<Shield />}
              description="Es sind aktuell keine ESET-Lizenzen verfügbar."
            />
          }
        />
      </section>

      {/* Seat Adjustment Dialog */}
      <Modal
        open={seatDialog !== null}
        onOpenChange={(open) => {
          if (!open) setSeatDialog(null)
        }}
        size="sm"
        title="Seat-Anzahl anpassen"
        description={
          seatDialog
            ? `${seatDialog.license.productName} – ${
                seatDialog.license.customerName ? a.name(seatDialog.license.customerName) : 'Unbekannt'
              }`
            : undefined
        }
        footer={
          seatDialog ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setSeatDialog(null)}>
                Abbrechen
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  updateSeats.mutate({
                    licenseId: seatDialog.license.id,
                    seats: seatDialog.newSeats,
                  })
                }
                disabled={updateSeats.isPending || seatDialog.newSeats < 1}
              >
                {updateSeats.isPending ? 'Wird gespeichert …' : 'Speichern'}
              </Button>
            </>
          ) : null
        }
      >
        {seatDialog ? (
          <Input
            type="number"
            min={1}
            label="Neue Seat-Anzahl"
            hint={`Aktuell: ${seatDialog.license.usedSeats} / ${seatDialog.license.seats} belegt`}
            value={seatDialog.newSeats}
            onChange={(e) =>
              setSeatDialog({ ...seatDialog, newSeats: Number(e.target.value) })
            }
          />
        ) : null}
      </Modal>
          </div>
        </TabsContent>

        <TabsContent value="kunden">
          <KundenSyncSection />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ─── Kunden-Sync ────────────────────────────────────────────────────────────

interface LocalCustomer {
  id: string
  name: string
  sapCardCode: string | null
  esetCustomerId: string | null
}

interface LocalCustomersResponse {
  items: LocalCustomer[]
  stats: { unlinked: number; total: number }
}

interface BulkSyncResponse {
  totalEsetCustomers: number
  localCandidates: number
  alreadyLinked: number
  matched: number
  unmatched: number
}

function KundenSyncSection() {
  const a = useAnonymize()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [bulkResult, setBulkResult] = useState<BulkSyncResponse | null>(null)

  const list = useQuery<LocalCustomersResponse>({
    queryKey: ['eset-local-customers'],
    queryFn: () => api.get('/eset/customers/local').then((r) => r.data),
    retry: false,
  })

  const bulkSync = useMutation({
    mutationFn: () => api.post('/eset/customers/bulk-sync', {}).then((r) => r.data as BulkSyncResponse),
    onSuccess: (data) => {
      setBulkResult(data)
      queryClient.invalidateQueries({ queryKey: ['eset-local-customers'] })
      toast({
        title: 'Bulk-Sync abgeschlossen',
        description: `${data.matched} verlinkt · ${data.alreadyLinked} bereits · ${data.unmatched} ohne Treffer`,
        tone: 'success',
      })
    },
    onError: (err: unknown) => {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Unbekannter Fehler')
          : err instanceof Error
            ? err.message
            : 'Unbekannter Fehler'
      toast({ title: 'Bulk-Sync fehlgeschlagen', description: message, tone: 'danger' })
    },
  })

  const syncOne = useMutation({
    mutationFn: (customerId: string) =>
      api.post(`/eset/customers/sync/${customerId}`, {}).then((r) => r.data),
    onSuccess: (data: { action: string; esetCustomerId: string }) => {
      queryClient.invalidateQueries({ queryKey: ['eset-local-customers'] })
      const label =
        data.action === 'created'
          ? 'Bei ESET angelegt'
          : data.action === 'linked_existing'
            ? 'Mit bestehendem ESET-Kunden verlinkt'
            : 'Bereits verlinkt'
      toast({ title: label, description: data.esetCustomerId, tone: 'success' })
    },
    onError: (err: unknown) => {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Unbekannter Fehler')
          : err instanceof Error
            ? err.message
            : 'Unbekannter Fehler'
      toast({ title: 'Sync fehlgeschlagen', description: message, tone: 'danger' })
    },
  })

  const items = list.data?.items ?? []
  const stats = list.data?.stats ?? { unlinked: 0, total: 0 }

  const columns = useMemo<ColumnDef<LocalCustomer, unknown>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Kunde',
        cell: ({ row }) => a.name(row.original.name),
      },
      textColumn<LocalCustomer>('sapCardCode', 'SAP CardCode'),
      {
        id: 'status',
        header: 'ESET-Status',
        size: 220,
        cell: ({ row }) =>
          row.original.esetCustomerId ? (
            <span className="inline-flex items-center gap-1.5 text-small text-green-700">
              <Link2 className="w-4 h-4" />
              Verlinkt ({row.original.esetCustomerId.slice(0, 8)}…)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-small text-gray-500">
              <Link2Off className="w-4 h-4" />
              Nicht verlinkt
            </span>
          ),
      },
      actionsColumn<LocalCustomer>(
        'Aktion',
        (c) =>
          c.esetCustomerId ? null : (
            <Button
              variant="ghost"
              size="xs"
              leadingIcon={<Link2 className="w-4 h-4" />}
              disabled={!c.sapCardCode || (syncOne.isPending && syncOne.variables === c.id)}
              onClick={() => syncOne.mutate(c.id)}
            >
              Bei ESET anlegen / verlinken
            </Button>
          ),
        { size: 240 },
      ),
    ],
    [a, syncOne],
  )

  return (
    <div className="space-y-4">
      <Card>
        <Card.Body className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-body font-semibold text-gray-900">
              Lokale Kunden ↔ ESET MSP verknüpfen
            </p>
            <p className="text-small text-gray-500">
              Bulk-Sync gleicht <code>sap_card_code</code> ↔ <code>customIdentifier</code> ab
              und verlinkt vorhandene ESET-Kunden. Anlage neuer Kunden bei ESET läuft
              pro Zeile über „Bei ESET anlegen / verlinken".
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<RefreshCcw className="w-4 h-4" />}
            loading={bulkSync.isPending}
            onClick={() => bulkSync.mutate()}
          >
            Bulk-Sync starten
          </Button>
        </Card.Body>
        {bulkResult ? (
          <Card.Footer className="text-small text-gray-700">
            <span className="font-medium">Letzter Lauf:</span> {bulkResult.matched} neu verlinkt ·{' '}
            {bulkResult.alreadyLinked} bereits · {bulkResult.unmatched} ohne Treffer ·{' '}
            EMA-Datensätze: {bulkResult.totalEsetCustomers}
          </Card.Footer>
        ) : null}
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard
          label="Lokale Kunden"
          value={stats.total}
          loading={list.isLoading}
          icon={<Users className="w-5 h-5 text-gray-500" />}
          tone="gray"
        />
        <StatCard
          label="Mit SAP, ohne ESET-Link"
          value={stats.unlinked}
          loading={list.isLoading}
          icon={<Link2Off className="w-5 h-5 text-yellow-500" />}
          tone={stats.unlinked > 0 ? 'yellow' : 'gray'}
        />
        <StatCard
          label="Verlinkt"
          value={items.filter((i) => i.esetCustomerId).length}
          loading={list.isLoading}
          icon={<Link2 className="w-5 h-5 text-green-500" />}
          tone="green"
        />
      </div>

      <DataTable<LocalCustomer>
        data={items}
        columns={columns}
        getRowId={(c) => c.id}
        loading={list.isLoading}
        error={list.isError ? 'Kunden konnten nicht geladen werden.' : undefined}
        onRetry={() => list.refetch()}
        emptyState={
          <EmptyState
            title="Keine Kunden vorhanden"
            icon={<Users />}
            description="Sobald lokale Kunden gepflegt sind, erscheinen sie hier."
          />
        }
      />
    </div>
  )
}

type StatTone = 'blue' | 'green' | 'red' | 'yellow' | 'gray'

const TONE_BG: Record<StatTone, string> = {
  blue: 'bg-blue-50',
  green: 'bg-green-50',
  red: 'bg-red-50',
  yellow: 'bg-yellow-50',
  gray: 'bg-gray-50',
}

function StatCard({
  label,
  value,
  loading,
  icon,
  tone = 'gray',
}: {
  label: string
  value: string | number | undefined
  loading?: boolean
  icon: React.ReactNode
  tone?: StatTone
}) {
  return (
    <Card>
      <Card.Body className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${TONE_BG[tone]}`}>{icon}</div>
        <div>
          <p className="text-caption text-gray-500">{label}</p>
          {loading ? (
            <div className="h-5 w-10 bg-gray-200 animate-pulse rounded mt-0.5" />
          ) : (
            <p className="text-h3 text-gray-900">{value ?? '—'}</p>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}

function Row({
  label,
  value,
  valueClassName,
}: {
  label: string
  value: React.ReactNode
  valueClassName?: string
}) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className={valueClassName ?? 'font-medium text-gray-900'}>{value}</span>
    </div>
  )
}
